import React from "react";
import {
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    RadioGroup,
    FormControlLabel,
    Radio,
    Box,
    Typography
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { getLocationFacilityDetails } from '../../utils/constants';

export const BreastReferTab = ({
    referDate,
    setReferDate,
    referTo,
    setReferTo,
    referReason,
    setReferReason,
    provisionalDiagnosis,
    setProvisionalDiagnosis,
    referredSpecialty,
    setReferredSpecialty,
    seekingPrivateCare,
    setSeekingPrivateCare,
    errors,
    setErrors,
    handleSaveReferral
}) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a202c', fontSize: '18px' }}>
                Refer
            </Typography>

            {/* Flex-based Refer Tab Form */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px', mt: 2, width: '100%' }}>
                {/* Date selection */}
                <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 16px)' } }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Date"
                            format="DD/MM/YYYY"
                            value={referDate ? dayjs(referDate, 'YYYY-MM-DD') : null}
                            onChange={(newValue) => setReferDate(newValue ? newValue.format('YYYY-MM-DD') : '')}
                            slotProps={{
                                textField: {
                                    variant: 'standard',
                                    fullWidth: true
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Box>

                {/* Referred To select */}
                <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 16px)' } }}>
                    <FormControl variant="standard" fullWidth required error={!!errors.referTo}>
                        <InputLabel sx={{
                            '& .MuiFormLabel-asterisk': {
                                color: 'red',
                            },
                        }}>
                            Referred To
                        </InputLabel>
                        <Select
                            value={referTo}
                            onChange={(e) => {
                                setReferTo(e.target.value);
                                if (errors.referTo) {
                                    setErrors(prev => ({ ...prev, referTo: null }));
                                }
                            }}
                            label="Referred To"
                        >
                            {(() => {
                                const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails") || localStorage.getItem("NCD_loggedinUserdetails") || "{}");
                                const { referFurtherToFacility } = getLocationFacilityDetails(loggedData);
                                return referFurtherToFacility.map((fac) => (
                                    <MenuItem key={fac.id} value={String(fac.id)}>{fac.name}</MenuItem>
                                ));
                            })()}
                        </Select>
                        {errors.referTo && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {errors.referTo}
                            </Typography>
                        )}
                    </FormControl>
                </Box>

                {/* Reason for Referral select */}
                <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 16px)' } }}>
                    <FormControl variant="standard" fullWidth required error={!!errors.referReason}>
                        <InputLabel sx={{
                            '& .MuiFormLabel-asterisk': {
                                color: 'red',
                            },
                        }}>
                            Reason for Referral
                        </InputLabel>
                        <Select
                            value={referReason}
                            onChange={(e) => {
                                setReferReason(e.target.value);
                                if (errors.referReason) {
                                    setErrors(prev => ({ ...prev, referReason: null }));
                                }
                            }}
                            label="Reason for Referral"
                        >
                            <MenuItem value="Unable to diagnose completely">Unable to diagnose completely</MenuItem>
                            <MenuItem value="Needs further tests">Needs further tests</MenuItem>
                            <MenuItem value="Further Treatment">Further Treatment</MenuItem>
                            <MenuItem value="The facility isn't conveniently located for the patient">The facility isn't conveniently located for the patient</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                        </Select>
                        {errors.referReason && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {errors.referReason}
                            </Typography>
                        )}
                    </FormControl>
                </Box>

                {/* Diagnosis (if/any) */}
                <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 16px)' } }}>
                    <TextField
                        variant="standard"
                        label="Diagnosis (if/any)"
                        placeholder="Enter Provisional Diagnosis"
                        value={provisionalDiagnosis}
                        onChange={(e) => setProvisionalDiagnosis(e.target.value)}
                        fullWidth
                    />
                </Box>

                {/* Referred Specialty select */}
                <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 16px)' } }}>
                    <FormControl variant="standard" fullWidth required error={!!errors.referredSpecialty}>
                        <InputLabel sx={{
                            '& .MuiFormLabel-asterisk': {
                                color: 'red',
                            },
                        }}>
                            Referred Specialty
                        </InputLabel>
                        <Select
                            value={referredSpecialty}
                            onChange={(e) => {
                                setReferredSpecialty(e.target.value);
                                if (errors.referredSpecialty) {
                                    setErrors(prev => ({ ...prev, referredSpecialty: null }));
                                }
                            }}
                            label="Referred Specialty"
                        >
                            <MenuItem value="Medical">Medical</MenuItem>
                            <MenuItem value="Surgery">Surgery</MenuItem>
                            <MenuItem value="Oncology">Oncology</MenuItem>
                            <MenuItem value="ENT">ENT</MenuItem>
                            <MenuItem value="Dental">Dental</MenuItem>
                        </Select>
                        {errors.referredSpecialty && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {errors.referredSpecialty}
                            </Typography>
                        )}
                    </FormControl>
                </Box>

                {/* Is patient seeking care in a private hospital? */}
                <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 16px)' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155', mb: 1 }}>
                        Is patient seeking care in a private hospital?
                    </Typography>
                    <RadioGroup
                        row
                        value={seekingPrivateCare}
                        onChange={(e) => setSeekingPrivateCare(e.target.value)}
                    >
                        <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                    </RadioGroup>
                </Box>
            </Box>

            {/* Refer Save button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 4 }}>
                <Button
                    onClick={handleSaveReferral}
                    variant="contained"
                    sx={{
                        bgcolor: '#3f51b5',
                        color: '#fff',
                        fontWeight: 700,
                        textTransform: 'none',
                        px: 4.5,
                        py: 1,
                        borderRadius: '4px',
                        '&:hover': { bgcolor: '#303f9f' }
                    }}
                >
                    SAVE
                </Button>
            </Box>
        </Box>
    );
};
